// Adapter routing table mirrors ml/adapters/. Every named task maps to one of
// the 16 fine-tuned LoRAs. The API server uses this map; we duplicate the
// names client-side only so the UI can show which adapter handled each turn.

export type AdapterName =
  | "answer-verifier"
  | "avatar-cue"
  | "concept-explainer"
  | "data-insights-generator"
  | "error-classifier"
  | "explainer"
  | "hint-generator"
  | "lesson-planner"
  | "mistake-coach"
  | "quant-generator"
  | "score-predictor"
  | "socratic-tutor"
  | "speech-rewriter"
  | "ssml-generator"
  | "verbal-generator"
  | "weakness-detector"
  | "whiteboard-renderer";

export interface AdapterMeta {
  name: AdapterName;
  label: string;
  description: string;
  verified: boolean; // PASS in eval, n >= 5
}

export const ADAPTERS: Record<AdapterName, AdapterMeta> = {
  "quant-generator": {
    name: "quant-generator",
    label: "Quant question writer",
    description: "Generates quantitative problems on demand with answer key.",
    verified: true,
  },
  "verbal-generator": {
    name: "verbal-generator",
    label: "Verbal question writer",
    description: "Creates Reading Comprehension and Critical Reasoning items.",
    verified: false,
  },
  "data-insights-generator": {
    name: "data-insights-generator",
    label: "Data Insights writer",
    description: "Crafts data sufficiency and graphics-based DI items.",
    verified: false,
  },
  explainer: {
    name: "explainer",
    label: "Solution explainer",
    description: "Walks through any GMAT problem step by step.",
    verified: true,
  },
  "concept-explainer": {
    name: "concept-explainer",
    label: "Concept teacher",
    description: "Teaches a single GMAT concept from scratch.",
    verified: false,
  },
  "hint-generator": {
    name: "hint-generator",
    label: "Hint coach",
    description: "Produces graduated hints without revealing the answer.",
    verified: true,
  },
  "socratic-tutor": {
    name: "socratic-tutor",
    label: "Socratic tutor",
    description: "Asks guiding questions until the student gets unstuck.",
    verified: false,
  },
  "answer-verifier": {
    name: "answer-verifier",
    label: "Answer verifier",
    description: "Confirms or flags claimed answer keys and explanations.",
    verified: true,
  },
  "error-classifier": {
    name: "error-classifier",
    label: "Error classifier",
    description: "Categorizes why a student missed a problem.",
    verified: true,
  },
  "mistake-coach": {
    name: "mistake-coach",
    label: "Mistake coach",
    description: "Gives reflective feedback on a wrong attempt.",
    verified: false,
  },
  "weakness-detector": {
    name: "weakness-detector",
    label: "Weakness detector",
    description: "Identifies skill gaps from recent performance.",
    verified: false,
  },
  "score-predictor": {
    name: "score-predictor",
    label: "Score predictor",
    description: "Projects an official GMAT score from diagnostic data.",
    verified: true,
  },
  "lesson-planner": {
    name: "lesson-planner",
    label: "Lesson planner",
    description: "Builds a personalized weekly study plan.",
    verified: false,
  },
  "avatar-cue": {
    name: "avatar-cue",
    label: "Avatar cue director",
    description: "Generates expression / gesture cues for the avatar.",
    verified: true,
  },
  "ssml-generator": {
    name: "ssml-generator",
    label: "SSML synthesizer",
    description: "Adds SSML markup to make speech sound natural.",
    verified: true,
  },
  "speech-rewriter": {
    name: "speech-rewriter",
    label: "Speech rewriter",
    description: "Rewrites text into spoken-friendly phrasing.",
    verified: false,
  },
  "whiteboard-renderer": {
    name: "whiteboard-renderer",
    label: "Whiteboard renderer",
    description: "Emits whiteboard drawing instructions for math work.",
    verified: false,
  },
};

export const VERIFIED_ADAPTERS = (
  Object.values(ADAPTERS).filter((a) => a.verified) as AdapterMeta[]
).map((a) => a.name);
